import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IconWifiOff, IconSpeakerphone, IconMenu2, IconBrandDiscord, IconRefresh, IconDownload, IconWorldWww, IconX, IconMessageCircle, IconBroadcast, IconLoader2, IconUsers } from '@tabler/icons-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { downloadCSV } from '../../../../utils/csvUtils';
import campaignService from '../../../../services/campaignService';
import discordService from '../../../../services/discordService';
import { apiClient } from '../../../../config/apiConfig';
import { useAuth } from '../../../../contexts/auth/AuthContext';
import PublicReportEditor from '../PublicReportEditor';
import { ConfirmDialog } from '../../../ui/confirm-dialog';

import CampaignView from './components/CampaignView';
import ManageGroupView from './components/ManageGroupView';
import DiscordPreviewModal from './components/DiscordPreviewModal';
import DiscordSyncModal from './components/DiscordSyncModal';
import useGroupManagement from './hooks/useGroupManagement';
import useGroupSocket from './hooks/useGroupSocket';
import CampaignOperationsSummary from '../CampaignOperationsSummary';

const AmplificationGroupFlow = ({ campaignData, onRefresh, isExternal = false, operationsSummary, operationsSummaryLoading = false, operationsSummaryError = '', onOperationsRefresh }) => {
 const { campaignId } = useParams();
 const { hasPermission } = useAuth();
 const canManageCampaigns = hasPermission('manage_campaigns');

 // View state
 const [view, setView] = useState('campaign'); // 'campaign' | 'manageGroup'
 const [selectedGroupId, setSelectedGroupId] = useState(null);
 const [showDiscordPreview, setShowDiscordPreview] = useState(false);
 const [discordGuildId, setDiscordGuildId] = useState('');

 useEffect(() => {
 if (campaignData?.campaignType === 'AMPLIFICATION') return;
 let mounted = true;
 discordService.getSettings()
 .then(settings => { if (mounted) setDiscordGuildId(settings?.guildId || ''); })
 .catch(() => {});
 return () => { mounted = false; };
 }, [campaignData?.campaignType]);

 // Group management hook - loads real data from API
 const {
 groups,
 creators,
 loading,
 activatingGroupId,
 createGroup,
 unassignCreator,
 bulkAssignCreators,
 submitCreatorPost,
 sendGoLiveMessage,
 duplicateGroup,
 deleteGroup,
 addCreator,
 setGroups,
 setCreators,
 addReactedCreator,
 fetchReactions,
 handleTweetSubmitted,
 handleContentActivity
 } = useGroupManagement(campaignId, campaignData);

 // Socket integration with amplification event routing
 const handleCampaignUpdate = useCallback(() => {
 // Generic campaign update — handled by specific event types in useGroupSocket
 }, []);

 const handleGroupActivated = useCallback((groupId, discord) => {
 setGroups(prev => prev.map(g =>
 g._id === groupId
 ? { ...g, status: 'active', discord, threadId: discord?.threadId || g.threadId }
 : g
 ));
 }, [setGroups]);

 const { connected: socketConnected } = useGroupSocket(campaignId, {
 onCampaignUpdate: handleCampaignUpdate,
 onNewReaction: addReactedCreator,
 onReactionSync: fetchReactions,
 onTweetSubmitted: handleTweetSubmitted,
 onContentActivity: handleContentActivity,
 onGroupActivated: handleGroupActivated
 });

 // Use campaignData directly as the campaign object
 const campaign = campaignData;

 // View navigation
 const handleManageGroup = (groupId) => {
 setSelectedGroupId(groupId);
 setView('manageGroup');
 };

 const handleBackToCampaign = () => {
 setSelectedGroupId(null);
 setView('campaign');
 };

 const handleGoLive = (groupId, options) => {
 return sendGoLiveMessage(groupId, options);
 };

 // Hamburger menu state
 const [showMenu, setShowMenu] = useState(false);
 const [syncing, setSyncing] = useState(false);
 const [showPublicReportModal, setShowPublicReportModal] = useState(false);
 const [confirmReportDiscard, setConfirmReportDiscard] = useState(false);
 const menuRef = useRef(null);
 const menuButtonRef = useRef(null);
 const publicReportTriggerRef = useRef(null);
 const publicReportDialogRef = useRef(null);
 const publicReportCloseRef = useRef(null);
 const publicReportDirtyRef = useRef(false);
 const publicReportSavingRef = useRef(false);
 const [publicReportSaving, setPublicReportSaving] = useState(false);

 const openPublicReport = useCallback((event) => {
 publicReportTriggerRef.current = event.currentTarget.closest('#amplification-campaign-actions')
 ? menuButtonRef.current
 : event.currentTarget;
 setShowMenu(false);
 setShowPublicReportModal(true);
 }, []);

 const handlePublicReportDirtyChange = useCallback((dirty) => {
 publicReportDirtyRef.current = dirty;
 }, []);

 const handlePublicReportSavingChange = useCallback((saving) => {
 publicReportSavingRef.current = saving;
 setPublicReportSaving(saving);
 }, []);

 const closePublicReport = useCallback(() => {
 if (publicReportSavingRef.current) return;
 if (publicReportDirtyRef.current) {
 setConfirmReportDiscard(true);
 return;
 }
 setShowPublicReportModal(false);
 publicReportDirtyRef.current = false;
 publicReportSavingRef.current = false;
 setPublicReportSaving(false);
 }, []);

 const discardPublicReport = useCallback(() => {
 if (publicReportSavingRef.current) return;
 publicReportDirtyRef.current = false;
 setConfirmReportDiscard(false);
 closePublicReport();
 }, [closePublicReport]);

 useEffect(() => {
 if (!showPublicReportModal) return undefined;
 const trigger = publicReportTriggerRef.current;
 publicReportCloseRef.current?.focus();

 const handleModalKeyDown = (event) => {
 const eventDialog = event.target?.closest?.('[role="dialog"]');
 if (eventDialog && eventDialog !== publicReportDialogRef.current) return;
 if (event.key === 'Escape') {
 if (publicReportSavingRef.current) return;
 queueMicrotask(() => {
 if (!event.defaultPrevented) closePublicReport();
 });
 return;
 }
 if (event.key !== 'Tab') return;

 const focusable = Array.from(publicReportDialogRef.current?.querySelectorAll(
 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
 ) || []);
 if (focusable.length === 0) return;
 const first = focusable[0];
 const last = focusable[focusable.length - 1];
 if (event.shiftKey && document.activeElement === first) {
 event.preventDefault();
 last.focus();
 } else if (!event.shiftKey && document.activeElement === last) {
 event.preventDefault();
 first.focus();
 }
 };

 document.addEventListener('keydown', handleModalKeyDown);
 return () => {
 document.removeEventListener('keydown', handleModalKeyDown);
 trigger?.focus();
 };
 }, [closePublicReport, showPublicReportModal]);

 // Creator import method state
 const [showDiscordSync, setShowDiscordSync] = useState(false);
 const [ampGoLiveOpen, setAmpGoLiveOpen] = useState(false);
 const [announcementText, setAnnouncementText] = useState('');
 const [postingAnnouncement, setPostingAnnouncement] = useState(false);

 // Import method detection — EXTERNAL campaigns skip Discord import entirely
 const hasNormalSync = campaign?.briefingMessage?.messageId;
 const hasAmpAnnouncement = campaign?.amplificationConfig?.announcement?.discordMessageId;
 const hasImportMethod = hasNormalSync || hasAmpAnnouncement;
 const showImportSelector = !isExternal && !hasImportMethod && creators.length === 0 && !loading;

 // Handle Amp Go Live — post Discord announcement
 const handleAmpGoLive = async () => {
 if (!announcementText.trim()) {
 toast.error('Please enter the announcement message');
 return;
 }
 setPostingAnnouncement(true);
 try {
 // Announcements must never silently change the Campaign type.
 if (campaign?.campaignType !== 'AMPLIFICATION') {
 toast.error('Announcements are only available for amplification campaigns');
 return;
 }
 const response = await apiClient.post(`/campaigns/${campaignId}/announcement`, {
 messageText: announcementText.trim()
 });
 toast.success(response.data?.action === 'edited'
 ? 'Announcement updated on Discord.'
 : 'Announcement posted to Discord. Telegram broadcast queued.');
 setAmpGoLiveOpen(false);
 setAnnouncementText('');
 if (onRefresh) await onRefresh();
 await fetchReactions();
 } catch (error) {
 console.error('Error posting announcement:', error);
 toast.error(error.response?.data?.error || 'Failed to post announcement');
 } finally {
 setPostingAnnouncement(false);
 }
 };

 // Handle Discord sync complete (normal campaign flow)
 const handleDiscordSyncComplete = async () => {
 setShowDiscordSync(false);
 if (onRefresh) await onRefresh();
 await fetchReactions();
 };

 // Manual impressions handler (workaround for Twitter API 429)
 const handleManualImpressions = useCallback(async (creatorSubdocId, impressions) => {
 const creator = creators.find(c => c._id === creatorSubdocId);
 const creatorId = creator?.creatorId || creatorSubdocId;
 await campaignService.updateManualImpressions(campaignId, creatorId, impressions);
 setCreators(prev => prev.map(c =>
 c._id === creatorSubdocId
 ? { ...c, tweet: { ...c.tweet, metrics: { ...c.tweet?.metrics, impressions } } }
 : c
 ));
 toast.success('Impressions updated');
 }, [campaignId, creators, setCreators]);

 useEffect(() => {
 const handleClickOutside = (e) => {
 if (menuRef.current && !menuRef.current.contains(e.target)) {
 setShowMenu(false);
 }
 };
 if (showMenu) document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
 }, [showMenu]);

 const handleSyncTwitterData = async () => {
 setShowMenu(false);
 setSyncing(true);
 try {
 const result = await campaignService.syncCampaignTwitterData(campaignId);
 const data = result?.data;
 if (data?.queued) {
 toast.success('X data sync started. Metrics will update as each batch finishes.');
 } else if (data?.result?.synced != null) {
 // Direct fallback completed because the queue was unavailable.
 await fetchReactions();
 const failed = data.result.failed ?? Math.max(0, data.result.total - data.result.synced);
 if (failed > 0) {
 toast.error(`Synced ${data.result.synced} of ${data.result.total} tweets. ${failed} could not refresh.`);
 } else {
 toast.success(`Synced ${data.result.synced} of ${data.result.total} tweets`);
 }
 } else {
 toast.success(data?.message || 'X data sync started');
 }
 } catch (err) {
 toast.error(`Sync failed: ${err.response?.data?.error || err.message}`);
 } finally {
 setSyncing(false);
 }
 };

 const handleExportReport = () => {
 setShowMenu(false);
 try {
 const groupMap = new Map(groups.map(g => [g._id, g]));
 const rows = [];

 // Sort creators by group
 const sortedCreators = [...creators]
 .filter(c => c.groupId)
 .sort((a, b) => (groupMap.get(a.groupId)?.name || '').localeCompare(groupMap.get(b.groupId)?.name || ''));

 // Build rows group by group with summary rows
 let currentGroupId = null;
 let groupCreators = [];

 const flushGroup = () => {
 if (!currentGroupId || groupCreators.length === 0) return;
 const g = groupMap.get(currentGroupId);
 const posted = groupCreators.filter(c => c.tweet?.url).length;
 const imp = groupCreators.reduce((s, c) => s + (c.tweet?.metrics?.impressions || 0), 0);
 const eng = groupCreators.reduce((s, c) => s + (c.tweet?.metrics?.likes || 0) + (c.tweet?.metrics?.retweets || 0) + (c.tweet?.metrics?.replies || 0), 0);

 // Group summary row
 rows.push({
 'Group': `${g?.name || 'Unknown'} (Summary)`,
 'Twitter Handle': '',
 'Content URL': '',
 'Status': g?.status || '',
 'Impressions': imp,
 'Likes': groupCreators.reduce((s, c) => s + (c.tweet?.metrics?.likes || 0), 0),
 'Retweets': groupCreators.reduce((s, c) => s + (c.tweet?.metrics?.retweets || 0), 0),
 'Replies': groupCreators.reduce((s, c) => s + (c.tweet?.metrics?.replies || 0), 0),
 'Engagement': eng,
 'Engagement Rate': imp > 0 ? `${(eng / imp * 100).toFixed(2)}%` : '0%',
 'Posted': `${posted}/${groupCreators.length}`
 });

 // Individual creator rows
 groupCreators.forEach(c => {
 const m = c.tweet?.metrics || {};
 const creatorEng = (m.likes || 0) + (m.retweets || 0) + (m.replies || 0);
 rows.push({
 'Group': g?.name || 'Unknown',
 'Twitter Handle': c.twitterHandle ? `@${c.twitterHandle}` : '',
 'Content URL': c.tweet?.url || '',
 'Status': c.deliveryStatus === 'PAID' ? 'Paid' : c.tweet?.url ? 'Posted' : 'Waiting',
 'Impressions': m.impressions || 0,
 'Likes': m.likes || 0,
 'Retweets': m.retweets || 0,
 'Replies': m.replies || 0,
 'Engagement': creatorEng,
 'Engagement Rate': (m.impressions || 0) > 0 ? `${(creatorEng / m.impressions * 100).toFixed(2)}%` : '0%',
 'Posted': c.tweet?.url ? 'Yes' : 'No'
 });
 });
 };

 sortedCreators.forEach(c => {
 if (c.groupId !== currentGroupId) {
 flushGroup();
 currentGroupId = c.groupId;
 groupCreators = [];
 }
 groupCreators.push(c);
 });
 flushGroup();

 if (rows.length === 0) {
 toast.error('No assigned creators to export');
 return;
 }

 const fileName = `${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_report`;
 downloadCSV(rows, fileName);
 toast.success('Report exported');
 } catch (err) {
 console.error('Export failed:', err);
 toast.error('Failed to export report');
 }
 };

 const handleExportReactedCreators = () => {
 setShowMenu(false);
 try {
 const reacted = creators.filter(c => !c.groupId);

 if (reacted.length === 0) {
 toast.error('No reacted creators to export');
 return;
 }

 const rows = reacted.map(c => ({
 'Twitter Handle': c.twitterHandle ? `@${c.twitterHandle}` : '',
 'Twitter Profile': c.twitterHandle ? `https://twitter.com/${c.twitterHandle}` : '',
 'Followers': c.followerCount || 0,
 'Score': c.advancedMetrics?.score ?? ''
 }));

 const fileName = `${campaign.name.replace(/[^a-zA-Z0-9]/g, '_')}_reacted_creators`;
 downloadCSV(rows, fileName);
 toast.success('Reacted creators exported');
 } catch (err) {
 console.error('Export failed:', err);
 toast.error('Failed to export reacted creators');
 }
 };

 // Computed values for header
 const groupCount = groups.length;
 const groupSize = groups[0]?.groupSize || 10;

 // Loading state
 if (loading || !campaign) {
 return (
 <div className="min-h-screen bg-surface-0 p-3 sm:p-6">
 <div className="max-w-6xl mx-auto">
 <div className="animate-pulse">
 <div className="bg-surface-1 h-28 mb-6" />
 <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
 {[1, 2, 3, 4].map(i => (
 <div key={i} className="bg-surface-1 h-24" />
 ))}
 </div>
 <div className="bg-surface-1 h-48 mb-6" />
 <div className="bg-surface-1 h-96" />
 </div>
 </div>
 </div>
 );
 }

 // Render view
 const renderView = () => {
 if (view === 'manageGroup' && selectedGroupId) {
 const group = groups.find(g => g._id === selectedGroupId);
 if (!group) {
 return (
 <div className="text-center py-12 text-text-secondary">
 Group not found.
 <button
 onClick={handleBackToCampaign}
 className="ml-2 text-accent hover:text-accent-hover underline"
 >
 Back to Campaign
 </button>
 </div>
 );
 }

 return (
 <ManageGroupView
 group={group}
 campaign={campaign}
 discordGuildId={discordGuildId}
 creators={creators}
 activating={activatingGroupId === group._id}
 onBack={handleBackToCampaign}
 onSubmitPost={submitCreatorPost}
 onGoLive={handleGoLive}
 onManualImpressions={handleManualImpressions}
 />
 );
 }

 return (
 <CampaignView
 campaign={campaign}
 groups={groups}
 creators={creators}
 onCreateGroup={createGroup}
 onManageGroup={handleManageGroup}
 onGroupAssign={bulkAssignCreators}
 onUnassignCreator={unassignCreator}
 onDuplicateGroup={duplicateGroup}
 onDeleteGroup={deleteGroup}
 onAddCreator={addCreator}
 />
 );
 };

 return (
 <div className="min-h-screen bg-surface-0 p-3 sm:p-6">
 <div className="max-w-6xl mx-auto">
 {/* Socket disconnection banner */}
 {!socketConnected && (
 <div className="bg-warning-muted border border-warning/30 p-3 mb-4 flex items-center gap-2 text-sm text-warning">
 <IconWifiOff size={18} />
 <span>Real-time updates paused. Reconnecting...</span>
 </div>
 )}

 {/* Campaign Header */}
 {view === 'campaign' && (
 <div className="bg-surface-1 border border-glass-border p-4 sm:p-6 mb-6">
 <div className="flex justify-between items-start gap-3">
 <div className="min-w-0 flex-1">
 <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
 <IconSpeakerphone size={28} className="text-accent flex-shrink-0" />
 <h1 className="text-xl sm:text-2xl font-bold text-text-primary truncate">
 {campaign.name}
 </h1>
 <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium bg-accent-muted text-accent border border-accent/30">
 {campaign.pricingType}
 </span>
 </div>
 <p className="text-sm text-text-secondary">
 {campaign.requiredCreators} creators &bull; {groupCount} groups &times; {groupSize}
 {campaign.endDate && (
 <> &bull; Due {format(parseISO(campaign.endDate), 'MMM d, h:mm a')}</>
 )}

 </p>
 </div>
 {canManageCampaigns && <button
 onClick={openPublicReport}
 className="hidden min-h-[44px] flex-shrink-0 items-center gap-2 border border-glass-border px-3 py-2 text-sm font-medium text-text-primary hover:bg-glass-bg-hover sm:inline-flex"
 >
 <IconWorldWww size={18} className="text-warning" />
 Edit public report
 </button>}
 <div className="relative flex-shrink-0" ref={menuRef}>
 <button
 ref={menuButtonRef}
 type="button"
 onClick={() => setShowMenu(!showMenu)}
 className="flex h-11 w-11 items-center justify-center border border-glass-border text-text-primary hover:bg-glass-bg-hover"
 aria-label="Campaign actions"
 aria-expanded={showMenu}
 aria-controls="amplification-campaign-actions"
 >
 <IconMenu2 size={20} />
 </button>
 {showMenu && (
 <div id="amplification-campaign-actions" className="absolute right-0 mt-2 w-56 bg-surface-1 border border-glass-border py-1 z-50">
 {!isExternal && campaign.amplificationConfig?.announcement?.discordMessageId && (
 <button
 onClick={() => { setShowMenu(false); setShowDiscordPreview(true); }}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3"
 >
 <IconBrandDiscord size={16} className="text-accent" />
 View Discord Post
 </button>
 )}
 {!isExternal && hasNormalSync && (
 <button
 onClick={() => { setShowMenu(false); setShowDiscordSync(true); }}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3"
 >
 <IconBrandDiscord size={16} className="text-accent" />
 Re-sync Discord Reactions
 </button>
 )}
 {!isExternal && hasAmpAnnouncement && (
 <button
 onClick={() => { setShowMenu(false); setAnnouncementText(campaign?.amplificationConfig?.announcement?.messageText || ''); setAmpGoLiveOpen(true); }}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3"
 >
 <IconBroadcast size={16} className="text-accent" />
 Update Announcement
 </button>
 )}
 <button
 onClick={handleSyncTwitterData}
 disabled={syncing}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3 disabled:opacity-50"
 >
 <IconRefresh size={16} className={`text-accent ${syncing ? 'animate-spin' : ''}`} />
 {syncing ? 'Syncing...' : 'Sync Twitter Data'}
 </button>
 <button
 onClick={handleExportReport}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3"
 >
 <IconDownload size={16} className="text-accent" />
 Download campaign data (CSV)
 </button>
 <button
 onClick={handleExportReactedCreators}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3"
 >
 <IconUsers size={16} className="text-accent" />
 Export Reacted Creators
 </button>
 {canManageCampaigns && <><div className="border-t border-glass-border my-1" />
 <button
 onClick={openPublicReport}
 className="w-full px-4 py-2.5 text-left text-sm text-text-primary hover:bg-glass-bg-hover flex items-center gap-3 sm:hidden"
 >
 <IconWorldWww size={16} className="text-warning" />
 Edit public report
 </button></>}
 </div>
 )}
 <ConfirmDialog
 open={showPublicReportModal && confirmReportDiscard}
 onOpenChange={setConfirmReportDiscard}
 onConfirm={discardPublicReport}
 title="Discard unsaved public report changes?"
 description="Only unsaved report edits will be discarded. Changes already saved to the campaign will remain."
 cancelText="Keep editing"
 confirmText="Discard changes"
 variant="destructive"
 loading={publicReportSaving}
 onEscapeKeyDown={(event) => {
 event.preventDefault();
 event.stopPropagation();
 if (!publicReportSavingRef.current) setConfirmReportDiscard(false);
 }}
 onCloseAutoFocus={(event) => {
 event.preventDefault();
 (publicReportCloseRef.current || publicReportTriggerRef.current)?.focus();
 }}
 />
 </div>
 </div>
 </div>
 )}

 {view === 'campaign' && (
 <CampaignOperationsSummary summary={operationsSummary} campaignId={campaignId} onRefresh={onOperationsRefresh} loading={operationsSummaryLoading} error={operationsSummaryError} onRetry={() => onOperationsRefresh?.()} />
 )}

 {/* Import Method Selector — shown when no creators and no import configured */}
 {showImportSelector && view === 'campaign' && (
 <div className="bg-surface-1 border border-glass-border p-4 sm:p-6 mb-6">
 <h2 className="text-lg font-semibold text-text-primary mb-1">Import Creators</h2>
 <p className="text-sm text-text-secondary mb-5">Choose how to recruit creators for this campaign</p>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 {/* Normal Campaign — Discord Message Sync */}
 <button
 onClick={() => setShowDiscordSync(true)}
 className="p-5 border border-glass-border bg-surface-0 hover:border-accent/50 hover:bg-accent-muted/30 transition-colors text-left group"
 >
 <div className="flex items-center gap-3 mb-3">
 <div className="w-10 h-10 flex items-center justify-center bg-accent-muted border border-accent/30">
 <IconMessageCircle size={22} className="text-accent" />
 </div>
 <h3 className="text-base font-semibold text-text-primary group-hover:text-accent">Normal Campaign</h3>
 </div>
 <p className="text-sm text-text-secondary">
 Paste a Discord message ID from the updates channel, sync reactions, and select which reaction to track for importing creators.
 </p>
 </button>

 {campaign?.campaignType === 'AMPLIFICATION' && <>{/* Amplification Campaign — Discord Announcement */}
 <button
 onClick={() => { setAnnouncementText(''); setAmpGoLiveOpen(true); }}
 className="p-5 border border-glass-border bg-surface-0 hover:border-accent/50 hover:bg-accent-muted/30 transition-colors text-left group"
 >
 <div className="flex items-center gap-3 mb-3">
 <div className="w-10 h-10 flex items-center justify-center bg-accent-muted border border-accent/30">
 <IconBroadcast size={22} className="text-accent" />
 </div>
 <h3 className="text-base font-semibold text-text-primary group-hover:text-accent">Amplification Campaign</h3>
 </div>
 <p className="text-sm text-text-secondary">
 Post an announcement to the amplification Discord channel. Creators react with the green tick to join the campaign.
 </p>
 </button>
 </>}
 </div>
 </div>
 )}

 {/* Main content */}
 {renderView()}

 {/* Discord Preview Modal */}
 {showDiscordPreview && (
 <DiscordPreviewModal
 campaign={campaign}
 onClose={() => setShowDiscordPreview(false)}
 />
 )}

 {/* Discord Sync Modal — Normal campaign flow */}
 {showDiscordSync && (
 <DiscordSyncModal
 campaign={campaign}
 onClose={() => setShowDiscordSync(false)}
 onSyncComplete={handleDiscordSyncComplete}
 />
 )}

 {/* Amp Go Live Dialog — Amplification campaign flow */}
 {ampGoLiveOpen && (
 <div
 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
 onClick={() => setAmpGoLiveOpen(false)}
 >
 <div
 className="bg-surface-1 border border-glass-border w-full max-w-lg mx-2 sm:mx-4 max-w-[calc(100vw-1rem)]"
 onClick={(e) => e.stopPropagation()}
 >
 <div className="flex justify-between items-center px-4 sm:px-6 py-4 border-b border-glass-border">
 <div className="flex items-center gap-2 min-w-0">
 <IconBroadcast size={22} className="text-accent flex-shrink-0" />
 <h2 className="text-lg font-bold text-text-primary truncate">
 {campaign?.amplificationConfig?.announcement?.discordMessageId ? 'Update Announcement' : 'Post Amplification Announcement'}
 </h2>
 </div>
 <button onClick={() => setAmpGoLiveOpen(false)} className="text-text-tertiary hover:text-text-primary flex-shrink-0">
 <IconX size={22} />
 </button>
 </div>
 <div className="px-4 sm:px-6 py-5 space-y-4">
 <p className="text-sm text-text-secondary">
 {campaign?.amplificationConfig?.announcement?.discordMessageId
 ? 'Edit the existing Discord announcement message.'
 : 'Compose and post the recruitment announcement to Discord, then send it on Telegram to creators with the amplification role.'}
 </p>
 <div>
 <label className="block text-sm font-medium text-text-secondary mb-1.5">Announcement Message</label>
 <textarea
 autoFocus
 rows={6}
 value={announcementText}
 onChange={(e) => setAnnouncementText(e.target.value)}
 placeholder="Write the recruitment message creators will see..."
 className="w-full px-3 py-2 border border-glass-border bg-surface-1 text-text-primary text-sm resize-y focus:ring-2 focus:ring-accent focus:border-transparent"
 />
 <p className="text-xs text-text-tertiary mt-1">
 This will be posted to the amplification Discord channel and mirrored to Telegram for amplification creators. Creators react on Discord to express interest.
 </p>
 </div>
 </div>
 <div className="flex justify-end gap-3 px-4 sm:px-6 py-4 border-t border-glass-border">
 <button
 onClick={() => setAmpGoLiveOpen(false)}
 className="px-4 py-2 text-sm font-medium border border-glass-border text-text-primary hover:bg-glass-bg-hover min-h-[44px]"
 >
 Cancel
 </button>
 <button
 onClick={handleAmpGoLive}
 disabled={postingAnnouncement || !announcementText.trim()}
 className="px-4 py-2 text-sm font-medium bg-accent hover:bg-accent-hover text-accent-foreground disabled:opacity-50 flex items-center gap-2 min-h-[44px]"
 >
 {postingAnnouncement && <IconLoader2 size={16} className="animate-spin" />}
 {campaign?.amplificationConfig?.announcement?.discordMessageId ? 'Update Announcement' : 'Post Announcement'}
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Public Report Config Modal */}
 {canManageCampaigns && showPublicReportModal && (
 <div
 className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
 onClick={closePublicReport}
 >
 <div
 ref={publicReportDialogRef}
 className="bg-surface-1 border border-glass-border max-w-5xl w-full mx-2 sm:mx-4 max-w-[calc(100vw-1rem)] max-h-[90vh] flex flex-col"
 onClick={(e) => e.stopPropagation()}
 role="dialog"
 aria-modal="true"
 aria-labelledby="public-report-editor-title"
 >
 <div className="flex justify-between items-center px-6 py-4 border-b border-glass-border">
 <h2 id="public-report-editor-title" className="text-lg font-bold text-text-primary">Edit public report</h2>
 <button
 ref={publicReportCloseRef}
 onClick={closePublicReport}
 disabled={publicReportSaving}
 className="flex h-11 w-11 items-center justify-center text-text-tertiary hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-50"
 aria-label="Close public report editor"
 >
 <IconX size={22} />
 </button>
 </div>
 <div className="overflow-y-auto p-6">
 <PublicReportEditor
 campaignId={campaignId}
 compact
 onDirtyChange={handlePublicReportDirtyChange}
 onSavingChange={handlePublicReportSavingChange}
 />
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 );
};

export default AmplificationGroupFlow;
